'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

// Utils
import dayjs from '@utils/dayjs-setup';
import { routes } from '@routes';
import { guestDescription } from '@utils/guest-description';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// UI
import { PlacesSelectAutocomplete, PlacesSelectAutocompleteFilters } from '@components/places-select-autocomplete';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { Button } from '@components/button';
import { Dropdown } from '@components/dropdown';
import { Typography } from '@components/typography';
import { SearchDateRangePicker, type FocusedInputShape, type SearchDateRange } from '@components/search-date-range-picker';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import { labels } from '@utils/constants';
type Props = {
  placeName?: string;
  placeId?: string;
  setCheckInDate?: (date: string | null) => void;
  setCheckOutDate?: (date: string | null) => void;
  // Optional pre-filled date range (YYYY-MM-DD). Used by campaign heroes that
  // want the search to default to a specific window (e.g. the 4th of July).
  initialCheckInDate?: string;
  initialCheckOutDate?: string;
  // Style overrides so campaign pages can re-theme the shared form (e.g. the
  // 4th of July hero swaps the teal card + primary button for navy/red).
  className?: string;
  submitClassName?: string;
};
export const SearchForm: React.FC<Props> = t0 => {
  const $ = _c(140);
  const {
    placeName,
    placeId,
    setCheckInDate,
    setCheckOutDate,
    initialCheckInDate,
    initialCheckOutDate,
    className,
    submitClassName
  } = t0;
  const t1 = placeId ? placeId : "";
  const t2 = placeName ? placeName : "";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      id: t1,
      name: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [place, setPlace] = React.useState(t3);
  const [placeError, setPlaceError] = React.useState(false);
  const [checkInDate, _setCheckInDate] = React.useState(initialCheckInDate ?? null);
  const [checkOutDate, _setCheckOutDate] = React.useState(initialCheckOutDate ?? null);
  const [dateRangePickerInputElement, _setDateRangePickerInputElement] = React.useState(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] = React.useState(null);
  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] = React.useState("");
  const [guestCountInputElement, _setGuestCountInputElement] = React.useState(null);
  const [guestCountFocusedInput, setGuestCountFocusedInput] = React.useState(false);
  const [adultCount, setAdultCount] = React.useState(1);
  const [childCount, setChildCount] = React.useState(0);
  const [infantCount, setInfantCount] = React.useState(0);
  const [petCount, setPetCount] = React.useState(0);
  let t4;
  let t5;
  if ($[3] !== adultCount || $[4] !== childCount || $[5] !== infantCount || $[6] !== petCount) {
    t4 = () => {
      const description = guestDescription(adultCount, childCount, infantCount, petCount);
      setGuestCountHumanReadableValue(description);
    };
    t5 = [adultCount, childCount, infantCount, petCount];
    $[3] = adultCount;
    $[4] = childCount;
    $[5] = infantCount;
    $[6] = petCount;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[9] !== setCheckInDate || $[10] !== setCheckOutDate) {
    t6 = range => {
      setCheckInDate && setCheckInDate(range.checkInDate);
      _setCheckInDate(range.checkInDate);
      setCheckOutDate && setCheckOutDate(range.checkOutDate);
      _setCheckOutDate(range.checkOutDate);
    };
    $[9] = setCheckInDate;
    $[10] = setCheckOutDate;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleDatesChange = t6;
  const handleValueChange = _temp;
  let t7;
  if ($[12] !== adultCount || $[13] !== checkInDate || $[14] !== checkOutDate || $[15] !== childCount || $[16] !== infantCount || $[17] !== petCount || $[18] !== place) {
    t7 = event => {
      event.preventDefault();
      if (!place) {
        setPlaceError(true);
        return;
      }
      window.location.assign(routes.searchShow({
        placeId: place.id,
        checkInDate: checkInDate || undefined,
        checkOutDate: checkOutDate || undefined,
        adultCount: adultCount > 0 ? adultCount : 1,
        childCount,
        infantCount,
        petCount
      }));
    };
    $[12] = adultCount;
    $[13] = checkInDate;
    $[14] = checkOutDate;
    $[15] = childCount;
    $[16] = infantCount;
    $[17] = petCount;
    $[18] = place;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onSubmit = t7;
  let t8;
  if ($[20] !== className) {
    t8 = twMerge("rounded-xl bg-primary-xlight/90 p-4 shadow-xl", className);
    $[20] = className;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== placeId || $[23] !== placeName) {
    t9 = placeName && placeId ? {
      label: extractFirstPhraseBeforeComma(placeName),
      value: placeId || ""
    } : null;
    $[22] = placeId;
    $[23] = placeName;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon name={IconNames.MAGNIFYING_GLASS} solid={true} className="text-primary-main" />;
    t11 = (_name, option) => {
      setPlaceError(false);
      if (option) {
        setPlace({
          name: option.label,
          id: option.value
        });
      }
    };
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  let t12;
  if ($[27] !== placeError || $[28] !== t9) {
    t12 = <div className="col-span-6 flex w-full whitespace-nowrap lg:col-span-2"><PlacesSelectAutocomplete initialValue={t9} placesFilter={PlacesSelectAutocompleteFilters.REGIONS} isInvalid={placeError} showLabel={false} name="place" placeholder="Where to?" noResultsLabel={labels.noResultsFound} data-testid="@search/search-input-place" leftContent={t10} onChange={t11} /></div>;
    $[27] = placeError;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== checkInDate) {
    t13 = checkInDate ? dayjs(checkInDate).format("MMM DD") : undefined;
    $[30] = checkInDate;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setDateRangePickerFocusedInput("startDate");
    };
    t15 = <Icon name={IconNames.CALENDAR_RANGE} className="text-primary-main" />;
    $[32] = t14;
    $[33] = t15;
  } else {
    t14 = $[32];
    t15 = $[33];
  }
  let t16;
  if ($[34] !== checkInDate || $[35] !== setCheckInDate) {
    t16 = checkInDate ? <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaDescribedby="clear-check-in-date-search-input" ariaLabel="Clear check-in date" onClick={() => {
      setCheckInDate && setCheckInDate(null);
      _setCheckInDate(null);
      setDateRangePickerFocusedInput("startDate");
    }} className="pe-0 ps-0" /> : null;
    $[34] = checkInDate;
    $[35] = setCheckInDate;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t13 || $[38] !== t16) {
    t17 = <Input label="Check-in" name="checkInDate" value={t13} onChange={_temp2} data-testid="@search/search-input-date" readOnly={true} inputClassName="text-primary-main bg-white" leftContentClassName="bg-white" rightContentClassName="bg-white" onClick={t14} leftContent={t15} rightContent={t16} autoComplete="off" hasCaret={false} />;
    $[37] = t13;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = !!dateRangePickerFocusedInput;
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      setDateRangePickerFocusedInput(null);
    };
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== checkInDate || $[42] !== checkOutDate || $[43] !== dateRangePickerFocusedInput || $[44] !== handleDatesChange) {
    t20 = <div className="px-2"><SearchDateRangePicker focusedInput={dateRangePickerFocusedInput} onFocusChange={setDateRangePickerFocusedInput} checkInDate={checkInDate} checkOutDate={checkOutDate} onDatesChange={handleDatesChange} /></div>;
    $[41] = checkInDate;
    $[42] = checkOutDate;
    $[43] = dateRangePickerFocusedInput;
    $[44] = handleDatesChange;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="flex w-full justify-end pb-2 pr-2"><Button variant="text" onClick={() => {
        _setCheckInDate(null);
        _setCheckOutDate(null);
        setDateRangePickerFocusedInput("startDate");
      }}>Clear</Button></div>;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== dateRangePickerInputElement || $[48] !== t18 || $[49] !== t20) {
    t22 = <div className="absolute mt-2"><Dropdown dropdownWidth="635px" height={350} reference={dateRangePickerInputElement} isOpen={t18} onClickAway={t19} data-id="date-range-picker-dropdown" className="absolute">{t20}{t21}</Dropdown></div>;
    $[47] = dateRangePickerInputElement;
    $[48] = t18;
    $[49] = t20;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t17 || $[52] !== t22) {
    t23 = <div ref={_setDateRangePickerInputElement} className="w-full min-w-[150px] lg:ml-2">{t17}{t22}</div>;
    $[51] = t17;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== checkOutDate) {
    t24 = checkOutDate ? dayjs(checkOutDate).format("MMM DD") : undefined;
    $[54] = checkOutDate;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== checkInDate) {
    t25 = () => {
      if (!checkInDate) {
        setDateRangePickerFocusedInput("startDate");
      } else {
        setDateRangePickerFocusedInput("endDate");
      }
    };
    $[56] = checkInDate;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Icon name={IconNames.CALENDAR_RANGE} className="text-primary-main" />;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== checkOutDate || $[60] !== setCheckOutDate) {
    t27 = checkOutDate ? <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaLabel="Clear check-out date" onClick={() => {
      setCheckOutDate && setCheckOutDate(null);
      _setCheckOutDate(null);
      setDateRangePickerFocusedInput("endDate");
    }} className="pe-0 ps-0" /> : null;
    $[59] = checkOutDate;
    $[60] = setCheckOutDate;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t24 || $[63] !== t25 || $[64] !== t27) {
    t28 = <div className="ml-1 w-full min-w-[140px] lg:ml-2"><Input label="Check-out" name="checkOutDate" data-testid="@search/check-out-date" value={t24} readOnly={true} inputClassName="text-primary-main bg-white" leftContentClassName="bg-white" rightContentClassName="bg-white" onClick={t25} leftContent={t26} rightContent={t27} autoComplete="off" hasCaret={false} /></div>;
    $[62] = t24;
    $[63] = t25;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  let t30;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      setGuestCountFocusedInput(true);
    };
    t30 = <Icon name={IconNames.USER} className="text-primary-main" />;
    $[66] = t29;
    $[67] = t30;
  } else {
    t29 = $[66];
    t30 = $[67];
  }
  let t31;
  if ($[68] !== guestCountHumanReadableValue) {
    t31 = guestCountHumanReadableValue ? <ButtonIcon variant="text" color="gray" size="sm" icon={IconNames.CIRCLE_X} iconSize="lg" ariaLabel="Clear guests" onClick={() => {
      setAdultCount(0);
      setChildCount(0);
      setInfantCount(0);
      setPetCount(0);
    }} className="pe-0 ps-0" /> : null;
    $[68] = guestCountHumanReadableValue;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== guestCountHumanReadableValue || $[71] !== t31) {
    t32 = <Input hasCaret={false} label="Who?" name="guests" value={guestCountHumanReadableValue} readOnly={true} inputClassName="text-primary-main bg-white" leftContentClassName="bg-white" rightContentClassName="bg-white" onClick={t29} leftContent={t30} rightContent={t31} autoComplete="off" />;
    $[70] = guestCountHumanReadableValue;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => {
      setGuestCountFocusedInput(false);
    };
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Adults</Typography><Typography variant="body2" component="p">Ages 13 or above</Typography></div>;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-adult" ariaLabel="Remove adult" variant="text" className="border-gray-main text-gray-main" onClick={() => setAdultCount(_temp3)} />;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_0 => {
      handleValueChange(e_0, setAdultCount, guestCapacity.ADULTS);
    };
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== adultCount) {
    t37 = <input type="number" aria-label="Number of adults" className="w-6 bg-white text-center" value={adultCount} data-testid="@search-form/adult-count" onChange={t36} />;
    $[77] = adultCount;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-adult" ariaLabel="Add adult" variant="text" className="border-gray-main text-gray-main" onClick={() => setAdultCount(_temp4)} />;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t37) {
    t39 = <div className="flex justify-between">{t34}<div className="flex justify-between space-x-2">{t35}{t37}{t38}</div></div>;
    $[80] = t37;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Children</Typography><Typography variant="body2" component="p">Ages 2-12</Typography></div>;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  let t41;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-child" ariaLabel="Remove child" variant="text" className="border-gray-main text-gray-main" onClick={() => setChildCount(_temp5)} />;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = e_1 => {
      handleValueChange(e_1, setChildCount, guestCapacity.CHILDREN);
    };
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== childCount) {
    t43 = <input type="number" aria-label="Number of children" className="w-6 bg-white text-center" data-testid="@search-form/child-count" value={childCount} onChange={t42} />;
    $[85] = childCount;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-child" ariaLabel="Add child" variant="text" className="border-gray-main text-gray-main" onClick={() => setChildCount(_temp6)} />;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] !== t43) {
    t45 = <div className="flex justify-between pt-4">{t40}<div className="flex justify-between space-x-2">{t41}{t43}{t44}</div></div>;
    $[88] = t43;
    $[89] = t45;
  } else {
    t45 = $[89];
  }
  let t46;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Infants</Typography><Typography variant="body2" component="p">Under 2</Typography></div>;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-infant" ariaLabel="Remove infant" variant="text" className="border-gray-main text-gray-main" onClick={() => setInfantCount(_temp7)} />;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = e_2 => {
      handleValueChange(e_2, setInfantCount, guestCapacity.INFANTS);
    };
    $[92] = t48;
  } else {
    t48 = $[92];
  }
  let t49;
  if ($[93] !== infantCount) {
    t49 = <input type="number" aria-label="Number of infants" className="w-6 bg-white text-center" value={infantCount} data-testid="@search-form/infant-count" onChange={t48} />;
    $[93] = infantCount;
    $[94] = t49;
  } else {
    t49 = $[94];
  }
  let t50;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-infant" ariaLabel="Add infant" variant="text" className="border-gray-main text-gray-main" onClick={() => setInfantCount(_temp8)} />;
    $[95] = t50;
  } else {
    t50 = $[95];
  }
  let t51;
  if ($[96] !== t49) {
    t51 = <div className="flex justify-between pt-4">{t46}<div className="flex justify-between space-x-2">{t47}{t49}{t50}</div></div>;
    $[96] = t49;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Pets</Typography><Typography variant="body2" component="p">Only properties that allow</Typography><Typography variant="body2" component="p">pets will be shown</Typography></div>;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  let t53;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-pet" ariaLabel="Remove pet" variant="text" className="border-gray-main text-gray-main" onClick={() => setPetCount(_temp9)} />;
    $[99] = t53;
  } else {
    t53 = $[99];
  }
  let t54;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = e_3 => {
      handleValueChange(e_3, setPetCount, guestCapacity.PETS);
    };
    $[100] = t54;
  } else {
    t54 = $[100];
  }
  let t55;
  if ($[101] !== petCount) {
    t55 = <input type="number" aria-label="Number of pets" className="w-6 bg-white text-center" value={petCount} data-testid="@search-form/pet-count" onChange={t54} />;
    $[101] = petCount;
    $[102] = t55;
  } else {
    t55 = $[102];
  }
  let t56;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-pet" ariaLabel="Add pet" variant="text" className="border-gray-main text-gray-main" onClick={() => setPetCount(_temp10)} />;
    $[103] = t56;
  } else {
    t56 = $[103];
  }
  let t57;
  if ($[104] !== t55) {
    t57 = <div className="flex justify-between pt-4">{t52}<div className="flex justify-between space-x-2">{t53}{t55}{t56}</div></div>;
    $[104] = t55;
    $[105] = t57;
  } else {
    t57 = $[105];
  }
  let t58;
  if ($[106] !== guestCountFocusedInput || $[107] !== guestCountInputElement || $[108] !== t39 || $[109] !== t45 || $[110] !== t51 || $[111] !== t57) {
    t58 = <div className="absolute right-0 mt-2 xl:right-auto"><Dropdown dropdownWidth="400px" reference={guestCountInputElement} isOpen={guestCountFocusedInput} onClickAway={t33} data-id="guest-count-picker-dropdown" className="space-y-4 divide-y divide-gray-light overflow-auto p-4 text-left">{t39}{t45}{t51}{t57}</Dropdown></div>;
    $[106] = guestCountFocusedInput;
    $[107] = guestCountInputElement;
    $[108] = t39;
    $[109] = t45;
    $[110] = t51;
    $[111] = t57;
    $[112] = t58;
  } else {
    t58 = $[112];
  }
  let t59;
  if ($[113] !== t32 || $[114] !== t58) {
    t59 = <div ref={_setGuestCountInputElement} className="ml-1 w-full min-w-[120px] lg:ml-2">{t32}{t58}</div>;
    $[113] = t32;
    $[114] = t58;
    $[115] = t59;
  } else {
    t59 = $[115];
  }
  let t60;
  if ($[116] !== submitClassName) {
    t60 = twMerge("ml-1 h-12", submitClassName);
    $[116] = submitClassName;
    $[117] = t60;
  } else {
    t60 = $[117];
  }
  let t61;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <Icon name={IconNames.MAGNIFYING_GLASS} />;
    $[118] = t61;
  } else {
    t61 = $[118];
  }
  let t62;
  if ($[119] !== t60) {
    t62 = <div className="flex items-center justify-center md:hidden"><Button type="submit" className={t60}>{t61}</Button></div>;
    $[119] = t60;
    $[120] = t62;
  } else {
    t62 = $[120];
  }
  let t63;
  if ($[121] !== submitClassName) {
    t63 = twMerge("ml-1 text-base lg:ml-2", submitClassName);
    $[121] = submitClassName;
    $[122] = t63;
  } else {
    t63 = $[122];
  }
  let t64;
  if ($[123] !== t63) {
    t64 = <div className="hidden md:flex"><Button type="submit" className={t63}>Search</Button></div>;
    $[123] = t63;
    $[124] = t64;
  } else {
    t64 = $[124];
  }
  let t65;
  if ($[125] !== t23 || $[126] !== t28 || $[127] !== t59 || $[128] !== t62 || $[129] !== t64) {
    t65 = <div className="col-span-6 flex w-full whitespace-nowrap lg:col-span-4">{t23}{t28}{t59}{t62}{t64}</div>;
    $[125] = t23;
    $[126] = t28;
    $[127] = t59;
    $[128] = t62;
    $[129] = t64;
    $[130] = t65;
  } else {
    t65 = $[130];
  }
  let t66;
  if ($[131] !== t12 || $[132] !== t65) {
    t66 = <div className="grid grid-cols-6 gap-2 lg:gap-0">{t12}{t65}</div>;
    $[131] = t12;
    $[132] = t65;
    $[133] = t66;
  } else {
    t66 = $[133];
  }
  let t67;
  if ($[134] !== onSubmit || $[135] !== t66) {
    t67 = <form onSubmit={onSubmit}>{t66}</form>;
    $[134] = onSubmit;
    $[135] = t66;
    $[136] = t67;
  } else {
    t67 = $[136];
  }
  let t68;
  if ($[137] !== t67 || $[138] !== t8) {
    t68 = <div className={t8}>{t67}</div>;
    $[137] = t67;
    $[138] = t8;
    $[139] = t68;
  } else {
    t68 = $[139];
  }
  return t68;
};
function _temp(e, setValue, maxValue) {
  e.preventDefault();
  const re = /^[0-9\b]+$/;
  if (e.target.value.startsWith("0")) {
    e.target.value = e.target.value.slice(1);
  }
  if (e.target.value === "" || re.test(e.target.value)) {
    const inputValue = Number(e.target.value);
    const value = inputValue > maxValue ? maxValue : inputValue;
    setValue(value);
  }
}
function _temp2() {}
function _temp3(val) {
  if (val > 0) {
    return val - 1;
  } else {
    return val;
  }
}
function _temp4(val_0) {
  return val_0 >= guestCapacity.ADULTS ? guestCapacity.ADULTS : val_0 + 1;
}
function _temp5(val_1) {
  if (val_1 > 0) {
    return val_1 - 1;
  } else {
    return val_1;
  }
}
function _temp6(val_2) {
  return val_2 >= guestCapacity.CHILDREN ? guestCapacity.CHILDREN : val_2 + 1;
}
function _temp7(val_3) {
  if (val_3 > 0) {
    return val_3 - 1;
  } else {
    return val_3;
  }
}
function _temp8(val_4) {
  return val_4 >= guestCapacity.INFANTS ? guestCapacity.INFANTS : val_4 + 1;
}
function _temp9(val_5) {
  if (val_5 > 0) {
    return val_5 - 1;
  } else {
    return val_5;
  }
}
function _temp10(val_6) {
  return val_6 >= guestCapacity.PETS ? guestCapacity.PETS : val_6 + 1;
}